import { ReactNode, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage, { EXPORT_PDF_QUERY_PARAM } from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import { InvoicePaymentApi, InvoicePaymentDataType } from "@/api/InvoicePaymentApi";
import { InvoicePaymentFormDataKeys } from "@/generated/InvoicePaymentFormDataKeys";
import { InvoicePaymentRelatedAttributes } from "@/formTypes/InvoicePaymentDataType";
import { formatDate } from "@utils/Api";
import { getPaymentMethodLabel } from "@utils/Utils";
import { formatMoneyValue } from "@utils/Currency";
import { CHEQUE_PAYMENT_WAY_CODE, CURRENCIES_CODES, SINPE_PAYMENT_WAY_CODE, TRANSFER_PAYMENT_WAY_CODE } from "@utils/Constants";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { checkInvoicePaymentTicketPrint } from "@utils/ticketPrintUtils";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { numberToWords } from "@utils/Recibe";
import { ClientStatusRowData, normalizeClientStatusRow } from "@utils/DocumentStatus";
import moment from "moment";
import { InvoiceApi } from "@/api/InvoiceApi";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";


const InvoicePaymentPrint = () => {
  const params = useParams()
  const { id: invoicePaymentId, autoPrint = false, copies = 0 } = params;
  const [searchParams] = useSearchParams();
  const exportFlag = searchParams.get(EXPORT_PDF_QUERY_PARAM);

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const [invoicePayment, setInvoicePayment] = useState<InvoicePaymentDataType>({})
  const [invoiceCalculateData, setInvoiceCalculateData] = useState<ClientStatusRowData>({} as ClientStatusRowData)

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadInvoicePaymentData()
  }, []);

  useEffect(() => {
    if (!exportFlag && autoPrint && suscription?.id && invoicePayment?.id) {
      checkInvoicePaymentTicketPrint(suscription, invoicePayment)
    }
  }, [suscription?.id, invoicePayment?.id]);

  const loadInvoicePaymentData = async () => {
    if (invoicePaymentId) {
      const invoicePayment = await InvoicePaymentApi.get(invoicePaymentId, {
        selectionSet: [
          ...InvoicePaymentFormDataKeys.filter(k => !InvoicePaymentRelatedAttributes.includes(k)),
          "currency.*",
          "invoice.*",
          "invoice.client.*",
          "creditOperator.*",
          "bankAccount.*"
        ]
      })
      const invoiceData = await InvoiceApi.get(`${invoicePayment.invoiceId}`, {
        selectionSet: [
          ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "creditnotes.*",
          "debitnotes.*",
          "invoicePayments.*",
          "interestEntries.*",
          "apliedCreditnote.*",
          "recibe.*"
        ]
      })
      const today = moment();
      const invoiceCalculatedData = await normalizeClientStatusRow(today.format("YYYY-MM-DD"),
        {
          kind: "invoice",
          data: invoiceData
        },
        {
          isInterestOnDue: false,
          interestOnDue: 0
        })
      setInvoiceCalculateData(invoiceCalculatedData)
      setInvoicePayment(invoicePayment)
    }
    PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
  }

  const renderPaymentWay = () => {
    let additionalInfo = "";
    const paymentWayLabel = getPaymentMethodLabel(invoicePayment.formaPago)
    if (invoicePayment.formaPago == `${TRANSFER_PAYMENT_WAY_CODE}`) {
      additionalInfo = ` #${invoicePayment.numAprobacion} Banco ${invoicePayment.bankAccount?.title} ${CURRENCIES_CODES[invoicePayment.bankAccount?.moneda]}`;
    } else if (invoicePayment.formaPago == `${CHEQUE_PAYMENT_WAY_CODE}`) {
      additionalInfo = ` #${invoicePayment.numAprobacion} Banco ${invoicePayment.bankAccount?.title} ${CURRENCIES_CODES[invoicePayment.bankAccount?.moneda]}`;
    } else if (invoicePayment.formaPago == `${SINPE_PAYMENT_WAY_CODE}`) {
      additionalInfo = ` #${invoicePayment.numAprobacion} Banco ${invoicePayment.bankAccount?.title} ${CURRENCIES_CODES[invoicePayment.bankAccount?.moneda]}`;
    }
    return (
      <div>
        <div className={styles.cuadro}>x</div> {paymentWayLabel} {additionalInfo}
      </div>
    )
  }

  const renderPage = (): ReactNode => {
    return (
      <PrintPage
        nula={invoicePayment.estado == "cancelled"}
        autoPrint={autoPrint === "1"}
        copies={Number(copies)}
      >
        <PrintPageHeader suscription={suscription} />
        <div className={styles.pageContent}>
          <PrintDocumentHeader
            title={`Pago Sobre Factura #${invoicePayment.invoice.numero} `}
            fecha={formatDate(invoicePayment.created, "YYYY-MM-DD", "DD.MM.YY")} />
          <div className={styles.info}>
            <div className={styles.clienteDetails}>
              <div className={styles.iconClient}>
                <FontAwesomeIcon icon={faUser} />
              </div>
              <div className={styles.clientes}>
                <div><span className={styles.clientTitle} >Recibimos de : </span> {invoicePayment.invoice.nombre_factura}</div>
                <div><span className={styles.clientTitle}>La suma de : </span> {numberToWords(Number(invoicePayment.monto || 0), invoicePayment.currency.currency_id)} </div>
                <div><span className={cx([styles.clientTitle, styles.concepto])}>Por Concepto de : </span> {invoicePayment.descripcion}</div>
              </div>
            </div>
            <div className={styles.totalWrapper}>
              <div className={styles.totalTitle}>Saldo:</div><div className={styles.totalAmount}> {formatMoneyValue(invoiceCalculateData.saldo + (invoicePayment.monto || 0), invoicePayment.currency.currency_id)} </div>
              <div className={styles.totalTitle}>Abono:</div><div className={styles.totalAmount}> {formatMoneyValue(invoicePayment.monto, invoicePayment.currency.currency_id)} </div>
              <div className={styles.totalTitle}>Saldo:</div><div className={styles.totalAmount}> {formatMoneyValue(Number(invoiceCalculateData.saldo), invoicePayment.currency.currency_id)} </div>
            </div>

            <div className={styles.clearFix}>
              <div className={styles.formasPago}>
                <div className="tipos">
                  <p>Forma de pago</p>
                  <div className={styles.formasPagoWrapper}>
                    {renderPaymentWay()}
                  </div>
                </div>

              </div>
              <div className={styles.signature}>______________________________________________________<br />Firma Responsable</div>
            </div>
          </div>
          <br />
          <p style={{
            textAlign: "center",
            fontSize: "12px",
            marginBottom: "15px"
          }}>Autorizado mediante Oficio #1197 de la D.G.T publicada en la Gaceta # 171 del 5-9-97           </p>

        </div>
      </PrintPage >
    )
  }

  return (
    <div>
      {invoicePayment?.id && renderPage()}
      {!invoicePayment.id && (
        <h3>Cargando </h3>
      )}

    </div>
  );
}

export default InvoicePaymentPrint;
